import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, Download, AlertCircle } from 'lucide-react';
import { useAuth } from '../services/contexts/AuthContext';

const AuthenticatedMediaPlayer = ({ mediaId, mediaType, className = "", onError }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [mediaUrl, setMediaUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const { token } = useAuth(); // Use AuthContext instead of localStorage
  const mediaRef = useRef(null);

  useEffect(() => {
    if (mediaId && token) {
      loadMedia();
    } else if (mediaId && !token) {
      setError('Authentication required');
      setIsLoading(false);
    }
    
    // Cleanup on unmount
    return () => {
      if (mediaUrl) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
  }, [mediaId, token]);

  const loadMedia = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🎵 Loading media file:', mediaId);
      console.log('🔍 useAuth token:', token);
      console.log('🔍 localStorage token:', localStorage.getItem('token'));
      console.log('🔍 localStorage user:', localStorage.getItem('user'));
      
      // Try multiple sources for the token
      let authToken = token;
      if (!authToken) {
        authToken = localStorage.getItem('token');
      }
      
      console.log('🔍 Final token to use:', !!authToken);
      
      if (!authToken) {
        throw new Error('No authentication token available');
      }
      
      // Fetch media with authentication headers
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/${mediaId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      console.log('🔍 Media fetch response:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Failed to load media: ${response.status} ${response.statusText}`);
      }

      // Get the blob
      const blob = await response.blob();
      console.log('✅ Media blob loaded:', {
        size: blob.size,
        type: blob.type
      });

      // Create object URL
      const url = URL.createObjectURL(blob);
      setMediaUrl(url);
      setIsLoading(false);

    } catch (err) {
      console.error('❌ Error loading media:', err);
      setError(err.message);
      setIsLoading(false);
      if (onError) {
        onError(err);
      }
    }
  };

  const togglePlayPause = () => {
    if (!mediaRef.current) return;

    if (isPlaying) {
      mediaRef.current.pause();
    } else {
      mediaRef.current.play();
    }
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);
  const handleEnded = () => setIsPlaying(false);

  const downloadMedia = () => {
    if (!mediaUrl) return;
    
    const link = document.createElement('a');
    link.href = mediaUrl;
    link.download = `media_${mediaId}.${mediaType === 'audio' ? 'webm' : 'webm'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center p-4 bg-gray-50 rounded-lg ${className}`}>
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-sm text-gray-600">Loading {mediaType}...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center justify-between p-4 bg-red-50 rounded-lg ${className}`}>
        <div className="flex items-center space-x-2">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <span className="text-sm text-red-600">Failed to load {mediaType}</span>
        </div>
        <button
          onClick={loadMedia}
          className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!mediaUrl) {
    return (
      <div className={`flex items-center justify-center p-4 bg-gray-50 rounded-lg ${className}`}>
        <span className="text-sm text-gray-500">No {mediaType} available</span>
      </div>
    );
  }

  return (
    <div className={`bg-gray-50 rounded-lg p-3 ${className}`}>
      {mediaType === 'audio' ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <button
              onClick={togglePlayPause}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              <span className="text-sm">{isPlaying ? 'Pause' : 'Play'}</span>
            </button>
            <button
              onClick={downloadMedia}
              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-blue-600 transition-colors"
            >
              <Download className="h-3 w-3" />
              <span>Download</span>
            </button>
          </div>
          <audio
            ref={mediaRef}
            src={mediaUrl}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            preload="metadata"
            className="w-full"
            controls
          />
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Video Recording</span>
            <button
              onClick={downloadMedia}
              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-blue-600 transition-colors"
            >
              <Download className="h-3 w-3" />
              <span>Download</span>
            </button>
          </div>
          <video
            ref={mediaRef}
            src={mediaUrl}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            controls
            preload="metadata"
            className="w-full max-w-md rounded"
          />
        </div>
      )}
    </div>
  );
};

export default AuthenticatedMediaPlayer;